import React from 'react';
import { Database, AlertTriangle, Edit3, Calculator } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { formatCurrency } from '../../lib/utils';

export function InitialStockCostDialog({ 
  open, 
  onOpenChange, 
  formData, 
  setFormData, 
  onSave,
  materials 
}) {
  const selectedMaterial = materials.find(m => m.id === formData.material_id);
  const overrideMode = !!formData.override;

  const setOverride = (v) => setFormData({ ...formData, override: v });

  // Factores de conversión de unidades de peso a libras
  const UNIT_TO_LB = {
    lb: 1.0,
    kg: 2.20462,
    g: 0.00220462,
    qq: 100.0,
    ton: 2204.62,
  };

  // ¿La unidad seleccionada es "saco" y el material tiene peso por saco definido?
  const isSackUnit = (formData.unit || '').toLowerCase() === 'saco';
  const sackWeight = selectedMaterial?.sack_weight || 0;
  const sackWeightUnit = (selectedMaterial?.sack_weight_unit || 'lb').toLowerCase();
  const sackWeightInLb = sackWeight * (UNIT_TO_LB[sackWeightUnit] || 1);
  const showSackHelper = isSackUnit && sackWeight > 0;

  // Handler cuando el usuario escribe en el campo "equivalente en libras"
  const handleLbEquivalentChange = (lbValue) => {
    const lb = parseFloat(lbValue) || 0;
    if (sackWeightInLb > 0) {
      const sacosEquivalent = lb / sackWeightInLb;
      setFormData({ ...formData, quantity: parseFloat(sacosEquivalent.toFixed(4)) });
    }
  };

  const quantityInLbPreview = showSackHelper ? (formData.quantity || 0) * sackWeightInLb : 0;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-md ${overrideMode ? 'border-t-4 border-t-amber-500' : ''}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {overrideMode ? (
              <><Edit3 className="h-5 w-5 text-amber-600" /> Sobrescribir Stock y Costo</>
            ) : (
              <><Database className="h-5 w-5 text-blue-600" /> Registrar Stock y Costo Inicial</>
            )}
          </DialogTitle>
          <DialogDescription>
            {overrideMode
              ? 'Reemplaza directamente el stock y costo actuales, sin calcular promedio ponderado.'
              : 'Agrega stock inicial y establece el costo (calcula promedio ponderado).'}
          </DialogDescription>
        </DialogHeader>

        {/* Toggle de modo */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg">
          <button
            type="button"
            onClick={() => setOverride(false)}
            className={`flex items-center justify-center gap-1 rounded-md py-2 text-sm font-medium transition-all ${!overrideMode ? 'bg-white shadow text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="mode-add-btn"
          >
            <Calculator className="h-4 w-4" />
            Sumar / Ponderar
          </button>
          <button
            type="button"
            onClick={() => setOverride(true)}
            className={`flex items-center justify-center gap-1 rounded-md py-2 text-sm font-medium transition-all ${overrideMode ? 'bg-white shadow text-amber-700' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="mode-override-btn"
          >
            <Edit3 className="h-4 w-4" />
            Sobrescribir
          </button>
        </div>

        <div className="space-y-4 py-2">
          {/* Material selector */}
          <div className="space-y-2">
            <Label>Material *</Label>
            <Select 
              value={formData.material_id} 
              onValueChange={(v) => {
                const mat = materials.find(m => m.id === v);
                setFormData({
                  ...formData, 
                  material_id: v,
                  unit: mat?.unit || 'qq',
                  currency: mat?.currency || 'DOP',
                  // En modo override, prellenar con valores actuales
                  ...(overrideMode && mat ? {
                    quantity: parseFloat((mat.current_stock || 0).toFixed(4)),
                    cost: parseFloat((mat.average_cost || 0).toFixed(4)),
                  } : {}),
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar material" />
              </SelectTrigger>
              <SelectContent>
                {materials.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Show current stock if material is selected */}
          {selectedMaterial && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-slate-500">Stock actual:</span>
                  <p className="font-semibold">{selectedMaterial.current_stock} {selectedMaterial.unit}</p>
                </div>
                <div>
                  <span className="text-slate-500">Costo actual:</span>
                  <p className="font-semibold">{formatCurrency(selectedMaterial.average_cost, selectedMaterial.currency)}/{selectedMaterial.unit}</p>
                </div>
              </div>
            </div>
          )}

          {/* Stock section */}
          <div className="border rounded-lg p-3 space-y-3">
            <h4 className="font-medium text-sm text-slate-700">
              {overrideMode ? 'Nuevo Stock Total' : 'Cantidad a Agregar'}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{overrideMode ? 'Stock' : 'Cantidad'} *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                  placeholder={overrideMode ? 'Ej: 250' : 'Ej: 100'}
                  data-testid="stock-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Unidad</Label>
                <Select 
                  value={formData.unit} 
                  onValueChange={(v) => setFormData({...formData, unit: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lb">Libras (lb)</SelectItem>
                    <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                    <SelectItem value="qq">Quintales (qq)</SelectItem>
                    <SelectItem value="ton">Toneladas (ton)</SelectItem>
                    <SelectItem value="saco">Sacos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Helper de conversión saco ↔ libras */}
            {showSackHelper && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2 text-xs text-blue-800">
                  <span>
                    Cada saco de <strong>{selectedMaterial.name}</strong> contiene{' '}
                    <strong>{sackWeight} {sackWeightUnit}</strong>
                    {sackWeightUnit !== 'lb' && (
                      <> (≈ {sackWeightInLb.toFixed(2)} lb)</>
                    )}
                    .
                  </span>
                  <span className="text-blue-700 whitespace-nowrap">
                    Existencia: <strong>{Number(selectedMaterial.current_stock || 0).toFixed(2)} sacos</strong>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs text-blue-700">
                      Equivalente en libras {overrideMode ? '(auto-convierte a sacos)' : ''}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={quantityInLbPreview > 0 ? quantityInLbPreview.toFixed(2) : ''}
                      onChange={(e) => handleLbEquivalentChange(e.target.value)}
                      placeholder="Ej: 45"
                      className="bg-white"
                      data-testid="stock-quantity-lb-input"
                    />
                  </div>
                  <div className="text-xs text-blue-700 pb-1">
                    {(formData.quantity || 0) > 0 ? (
                      <>
                        = <strong>{Number(formData.quantity).toFixed(4)} sacos</strong>
                        <div className="text-blue-600 mt-0.5">
                          ({quantityInLbPreview.toFixed(2)} lb ÷ {sackWeightInLb.toFixed(2)} lb/saco)
                        </div>
                      </>
                    ) : (
                      <span className="text-blue-500">Digita libras o sacos</span>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-blue-600">
                  Tip: si te quedan <strong>45 lb</strong> sueltas de un saco de {sackWeightInLb.toFixed(0)} lb, digita 45 aquí y se guardará como {(45 / sackWeightInLb).toFixed(4)} sacos.
                </p>
              </div>
            )}
          </div>

          {/* Cost section */}
          <div className="border rounded-lg p-3 space-y-3">
            <h4 className="font-medium text-sm text-slate-700">
              {overrideMode ? 'Nuevo Costo Promedio' : 'Costo por Unidad'}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Costo *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({...formData, cost: parseFloat(e.target.value) || 0})}
                  placeholder="Ej: 25.50"
                  data-testid="stock-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(v) => setFormData({...formData, currency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DOP">RD$ (Pesos)</SelectItem>
                    <SelectItem value="USD">US$ (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Preview of new values */}
          {selectedMaterial && formData.quantity > 0 && !overrideMode && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h4 className="font-medium text-sm text-green-800 mb-2">Resultado después de guardar:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-green-600">Nuevo stock:</span>
                  <p className="font-bold text-green-800">
                    {(selectedMaterial.current_stock + formData.quantity).toFixed(2)} {formData.unit}
                  </p>
                </div>
                {formData.cost > 0 && (() => {
                  const currentStock = selectedMaterial.current_stock || 0;
                  const currentCost = selectedMaterial.average_cost || 0;
                  const newQuantity = formData.quantity;
                  const newCost = formData.cost;
                  const newStock = currentStock + newQuantity;
                  const totalValue = (currentStock * currentCost) + (newQuantity * newCost);
                  const weightedAvgCost = newStock > 0 ? totalValue / newStock : newCost;
                  return (
                    <div>
                      <span className="text-green-600">Costo promedio ponderado:</span>
                      <p className="font-bold text-green-800">
                        {formatCurrency(weightedAvgCost, formData.currency)}/{formData.unit}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        ({currentStock.toFixed(2)}×{currentCost.toFixed(2)} + {newQuantity}×{newCost.toFixed(2)}) / {newStock.toFixed(2)}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Preview en modo override */}
          {selectedMaterial && overrideMode && (formData.quantity >= 0 || formData.cost >= 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="font-medium text-sm text-amber-800 mb-2">Resultado (sobrescritura directa):</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-amber-600">Stock:</span>
                  <p className="font-bold text-amber-800 line-through decoration-red-400/60">
                    {selectedMaterial.current_stock} {selectedMaterial.unit}
                  </p>
                  <p className="font-bold text-amber-900">
                    → {(formData.quantity || 0).toFixed(2)} {formData.unit}
                  </p>
                </div>
                <div>
                  <span className="text-amber-600">Costo:</span>
                  <p className="font-bold text-amber-800 line-through decoration-red-400/60">
                    {formatCurrency(selectedMaterial.average_cost, selectedMaterial.currency)}/{selectedMaterial.unit}
                  </p>
                  <p className="font-bold text-amber-900">
                    → {formatCurrency(formData.cost || 0, formData.currency)}/{formData.unit}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className={`border rounded-lg p-3 ${overrideMode ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`}>
            <p className={`text-sm flex items-start gap-2 ${overrideMode ? 'text-amber-800' : 'text-blue-800'}`}>
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              {overrideMode ? (
                <span>
                  <strong>Modo Sobrescribir activo.</strong> El stock y costo actuales serán <strong>reemplazados</strong> por los valores ingresados. No se calculará promedio ponderado. Esta acción no queda registrada como movimiento de inventario.
                </span>
              ) : (
                <span>La cantidad se <strong>sumará</strong> al stock actual. El costo se calculará como <strong>promedio ponderado</strong>.</span>
              )}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={onSave} 
            className={overrideMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}
            disabled={!formData.material_id || (!overrideMode && formData.quantity <= 0)}
            data-testid="save-stock-cost-btn"
          >
            {overrideMode ? <Edit3 className="h-4 w-4 mr-2" /> : <Database className="h-4 w-4 mr-2" />}
            {overrideMode ? 'Sobrescribir' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
